import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ExternalLink, LibraryBig, Plus, Save, Trash2 } from 'lucide-react';
import {
  CONFIGURABLE_JOURNEY_CATEGORIES,
  CONFIGURABLE_JOURNEY_LIBRARY as BASE_CONFIGURABLE_JOURNEY_LIBRARY,
  configurableJourneyRoute as baseConfigurableJourneyRoute,
  createConfigurableJourney as baseCreateConfigurableJourney,
} from '../../../src/data/configurableJourneyLibrary.js';
import {
  NEONATAL_STANDARD_REVIEW_TEMPLATE,
  createNeonatalStandardJourney,
} from '../../../src/data/neonatalStandardJourney.js';
import { NHNN_STUDIO_STARTERS, getNHNNStudioStarter, createNHNNStudioStarterJourney } from '../lib/nhnnStudioStarters.js';

const CONFIGURABLE_JOURNEY_LIBRARY = Object.freeze([
  ...BASE_CONFIGURABLE_JOURNEY_LIBRARY,
  ...NHNN_STUDIO_STARTERS,
  NEONATAL_STANDARD_REVIEW_TEMPLATE,
]);
const JOURNEY_CATEGORIES = { ...CONFIGURABLE_JOURNEY_CATEGORIES, nhnn_pilot: 'NHNN existing pilot drafts' };
const createConfigurableJourney = id =>
  id === NEONATAL_STANDARD_REVIEW_TEMPLATE.id ? createNeonatalStandardJourney()
    : getNHNNStudioStarter(id) ? createNHNNStudioStarterJourney(id)
    : baseCreateConfigurableJourney(id);
const configurableJourneyRoute = id =>
  id === NEONATAL_STANDARD_REVIEW_TEMPLATE.id ? NEONATAL_STANDARD_REVIEW_TEMPLATE.route
    : getNHNNStudioStarter(id)?.route || baseConfigurableJourneyRoute(id);
import { encodeJourneyPreviewPayload } from '../../../src/lib/journeyPreviewPayload.js';
import JourneyClinicalSummaryReview from './JourneyClinicalSummaryReview';
import { loadTrustConfig, saveTrustConfig } from '../lib/trustConfigStore';
import { loadCloudTrustConfig, saveCloudTrustConfig, attestPublishedJourneys } from '../lib/trustConfigApi';
import { trusts } from '../lib/trustLibrary';
import { roleByKey } from '../lib/library';
import { hasStudioRole, visibleTrusts } from '../lib/accessApi';
import { compactJourneyOverlay, countJourneyOverlayChanges, createJourneyOverlay } from '../lib/journeyOverlay';
import { applyJourneyOverlay } from '../../../src/data/journeyOverlay.js';
import { clinicalJourneyFingerprint } from '../../../src/lib/clinicalJourneyReviewSnapshot.js';
import '../journeyStyles.css';
import '../journeyOverlayStyles.css';

const patientAppBaseUrl = (import.meta.env.VITE_PATIENT_APP_URL || 'https://knowyourcare.co.uk').replace(/\/$/, '');
const getRoleId = (role) => typeof role === 'string' ? role : role?.id;
const templatesForTrust = trustId => CONFIGURABLE_JOURNEY_LIBRARY.filter(template =>
  template.category !== 'nhnn_pilot' || trustId === 'nhnn_uclh');

const humaniseRoleId = (id) => String(id || '')
  .replaceAll('_', ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const getRoleTitle = (role) => {
  const id = getRoleId(role);
  if (!id) return 'Role';
  return (typeof role !== 'string' ? role?.title : null)
    || roleByKey.get(id)?.title
    || humaniseRoleId(id);
};

function applyTrustDefaults(overlay, trust) {
  if (!overlay || !trust) return overlay;
  const trustName = trust.name || trust.shortName || 'Your Trust';
  const serviceName = trust.shortName || trust.name || 'Your hospital or service';
  return {
    ...overlay,
    serviceName: !overlay.serviceName || overlay.serviceName === 'Your hospital or service' ? serviceName : overlay.serviceName,
    branding: {
      ...overlay.branding,
      trustName: !overlay.branding?.trustName || overlay.branding.trustName === 'Your Trust' ? trustName : overlay.branding.trustName,
      logoAlt: !overlay.branding?.logoAlt || overlay.branding.logoAlt === 'Your Trust logo' ? `${trustName} logo` : overlay.branding.logoAlt,
    },
  };
}

function createStudioJourneyDraft(template, configuration, trust) {
  const source = createConfigurableJourney(template.id);
  if (!source) return null;
  const previous = configuration?.journeys?.[template.id];
  // Seed Trust links from the pre-existing NHNN demos on first addition.
  // No automatic Trust approval or patient publication is performed.
  const starter = !previous && template.category === 'nhnn_pilot'
    ? {
        serviceName: source.hospitalName,
        branding: { trustName: source.trustName },
        linkOverrides: Object.fromEntries((source.usefulLinks || []).map(link => [
          link.id, { title: link.title, description: link.description || '', url: link.url || '', visible: true },
        ])),
        contentComment: 'Imported from existing private NHNN demonstration; local clinical review required.',
      }
    : {};
  return applyTrustDefaults(createJourneyOverlay(template, source, previous || starter), trust);
}

function mergeJourney(current, patch) {
  return {
    ...current,
    ...patch,
    branding: { ...(current?.branding || {}), ...(patch.branding || {}) },
    roleOverrides: { ...(current?.roleOverrides || {}), ...(patch.roleOverrides || {}) },
    linkOverrides: { ...(current?.linkOverrides || {}), ...(patch.linkOverrides || {}) },
    stageOverrides: { ...(current?.stageOverrides || {}), ...(patch.stageOverrides || {}) },
  };
}

export default function JourneyLibraryStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = trusts }) {
  const scopedTrusts = visibleTrusts(studioAccess, studioTrusts);
  const [trustId, setTrustId] = useState(scopedTrusts.find((trust) => trust.id !== 'generic_nhs')?.id || scopedTrusts[0]?.id || 'generic_nhs');
  const [trustConfig, setTrustConfig] = useState(() => loadTrustConfig(trustId));
  const [selectedJourneyId, setSelectedJourneyId] = useState(CONFIGURABLE_JOURNEY_LIBRARY[0]?.id || '');
  const [journeyDraft, setJourneyDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [bulkReviewConfirmed, setBulkReviewConfirmed] = useState(false);
  const [bulkReviewBusy, setBulkReviewBusy] = useState(false);
  const reviewOnly = hasStudioRole(studioAccess, 'content_reviewer') && !hasStudioRole(studioAccess, 'trust_admin') && !studioAccess?.roles?.includes('platform_admin');
  const locked = trustConfig.status !== 'draft';
  // An authenticated reviewer may sign off unmodified, currently published
  // wording without reopening or republishing the Trust configuration.
  const reviewLocked = dirty || (locked && trustConfig.status !== 'published');
  const selectedTrust = scopedTrusts.find((trust) => trust.id === trustId) || null;
  const configuredCount = Object.keys(trustConfig.journeys || {}).length;
  const publishedReviewItems = Object.entries(trustConfig.journeys || {})
    .filter(([id, item]) => id !== 'neonatal-care' && item?.included !== false)
    .map(([id, item]) => ({
      id,
      title: CONFIGURABLE_JOURNEY_LIBRARY.find(journey => journey.id === id)?.title || item.title || id,
      contentFingerprint: clinicalJourneyFingerprint(item),
    }));
  const canAttestPublished = trustConfig.status === 'published'
    && hasStudioRole(studioAccess, 'content_reviewer')
    && publishedReviewItems.length > 0;
  const journeyLimit = Number.isInteger(selectedTrust?.journeyLimit) && selectedTrust.journeyLimit > 0 ? selectedTrust.journeyLimit : null;

  const permittedJourneyIds = studioAccess?.journeyIds || [];
  const availableTemplates = useMemo(() => {
    const eligible = templatesForTrust(trustId);
    return reviewOnly
      ? eligible.filter((journey) => trustConfig.journeys?.[journey.id] && permittedJourneyIds.includes(journey.id))
      : eligible;
  }, [reviewOnly, trustConfig.journeys, studioAccess?.journeyIds, trustId]);

  const selectedTemplate = availableTemplates.find((item) => item.id === selectedJourneyId)
    || availableTemplates[0]
    || null;
  const genericJourney = selectedTemplate ? createConfigurableJourney(selectedTemplate.id) : null;
  const savedOverlay = selectedTemplate ? trustConfig.journeys?.[selectedTemplate.id] : null;
  const configured = Boolean(savedOverlay);
  const limitReached = Boolean(!configured && journeyLimit && configuredCount >= journeyLimit);

  const buildDraft = (template, configuration = trustConfig) => {
    if (!template) return null;
    return createStudioJourneyDraft(template, configuration, selectedTrust);
  };

  const selectJourney = (journeyId, configuration = trustConfig, { force = false } = {}) => {
    if (!force && dirty && !window.confirm('Discard unsaved changes and open another Journey?')) return false;
    const template = availableTemplates.find((item) => item.id === journeyId) || availableTemplates[0];
    if (!template) {
      setSelectedJourneyId('');
      setJourneyDraft(null);
      setDirty(false);
      return false;
    }
    setSelectedJourneyId(template.id);
    setJourneyDraft(buildDraft(template, configuration));
    setDirty(false);
    return true;
  };

  const loadTrust = async (nextTrustId) => {
    setBulkReviewConfirmed(false);
    setTrustId(nextTrustId);
    let next = loadTrustConfig(nextTrustId);
    try {
      const response = await loadCloudTrustConfig(nextTrustId);
      next = saveTrustConfig(nextTrustId, response.configuration);
      setNotice(reviewOnly ? 'Journey review workspace loaded from the cloud.' : 'Journey configuration loaded from the cloud.');
    } catch {
      setNotice(reviewOnly ? 'Cloud review data is unavailable.' : 'Cloud configuration is unavailable. Using the browser fallback.');
    }
    setTrustConfig(next);
    const eligible = templatesForTrust(nextTrustId);
    const nextTemplates = reviewOnly ? eligible.filter((journey) => next.journeys?.[journey.id] && permittedJourneyIds.includes(journey.id)) : eligible;
    const preferred = nextTemplates.find((journey) => next.journeys?.[journey.id]) || nextTemplates[0];
    setSelectedJourneyId(preferred?.id || '');
    setJourneyDraft(preferred ? createStudioJourneyDraft(preferred, next, scopedTrusts.find((trust) => trust.id === nextTrustId) || null) : null);
    setDirty(false);
  };

  useEffect(() => { loadTrust(trustId); }, []);

  const updateDraft = (patch) => {
    if (reviewOnly || locked || !journeyDraft) return;
    setJourneyDraft((current) => mergeJourney(current, patch));
    setDirty(true);
  };

  const persistJourney = async ({ advance = false } = {}) => {
    if (reviewOnly || locked || !selectedTemplate || !genericJourney || !journeyDraft) return false;
    if (!configured && limitReached) {
      setNotice(`This Trust licence currently allows ${journeyLimit} Journey${journeyLimit === 1 ? '' : 's'}. Ask the Know Your Care platform administrator to enable additional Journeys.`);
      return false;
    }
    setSaving(true);
    const nextJourney = {
      ...journeyDraft,
      // An approved Waiting for Surgery review cannot survive a later edit.
      ...(selectedTemplate.unlisted ? { clinicalReview: null, clinicalReviews: {} } : {}),
      updatedAt: new Date().toISOString(),
    };
    const next = { ...trustConfig, journeys: { ...(trustConfig.journeys || {}), [selectedTemplate.id]: nextJourney } };
    const localSaved = saveTrustConfig(trustId, next);
    setTrustConfig(localSaved);
    try {
      const response = await saveCloudTrustConfig(trustId, localSaved);
      const cloudSaved = saveTrustConfig(trustId, response.configuration);
      setTrustConfig(cloudSaved);
      setJourneyDraft(buildDraft(selectedTemplate, cloudSaved));
      setDirty(false);
      setNotice(`${nextJourney.title} saved securely to the cloud.`);
      if (advance) advanceFrom(selectedTemplate.id, cloudSaved);
      return true;
    } catch (error) {
      setNotice(error.message || `${nextJourney.title} saved in this browser. Cloud sync is required before publication.`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addJourney = async () => {
    if (limitReached) {
      setNotice(`This Trust licence currently allows ${journeyLimit} Journey${journeyLimit === 1 ? '' : 's'}. Ask the Know Your Care platform administrator to enable additional Journeys.`);
      return;
    }
    setDirty(true);
    await persistJourney();
  };

  const removeJourney = async () => {
    if (reviewOnly || locked || !selectedTemplate || !configured) return;
    if (!window.confirm(`Remove ${selectedTemplate.title} from this Trust configuration?`)) return;
    const journeys = { ...(trustConfig.journeys || {}) };
    delete journeys[selectedTemplate.id];
    const localSaved = saveTrustConfig(trustId, { ...trustConfig, journeys });
    setTrustConfig(localSaved);
    try {
      const response = await saveCloudTrustConfig(trustId, localSaved);
      const cloudSaved = saveTrustConfig(trustId, response.configuration);
      setTrustConfig(cloudSaved);
      setJourneyDraft(buildDraft(selectedTemplate, cloudSaved));
      setDirty(false);
      setNotice(`${selectedTemplate.title} removed and saved securely to the cloud.`);
    } catch {
      setNotice(`${selectedTemplate.title} removed in this browser. Cloud sync is required before publication.`);
    }
  };

  const advanceFrom = (journeyId, configuration = trustConfig) => {
    const eligible = templatesForTrust(trustId);
    const list = reviewOnly ? eligible.filter((journey) => configuration.journeys?.[journey.id] && permittedJourneyIds.includes(journey.id)) : eligible;
    if (!list.length) return;
    const index = Math.max(0, list.findIndex((item) => item.id === journeyId));
    const next = list[(index + 1) % list.length];
    setSelectedJourneyId(next.id);
    setJourneyDraft(createStudioJourneyDraft(next, configuration, selectedTrust));
    setDirty(false);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const nextJourney = async () => {
    if (dirty) {
      const saved = await persistJourney({ advance: true });
      if (!saved) return;
    } else if (selectedTemplate) {
      advanceFrom(selectedTemplate.id);
    }
  };

  const confirmPublishedReviews = async () => {
    if (!canAttestPublished || !bulkReviewConfirmed || bulkReviewBusy || dirty) return;
    if (!window.confirm(`Confirm clinical review of all ${publishedReviewItems.length} listed, currently published Journeys for ${selectedTrust?.name || trustId}? Your account and date will be recorded as the clinical reviewer. This cannot be undone by closing this window.`)) return;
    setBulkReviewBusy(true);
    setNotice('');
    try {
      const response = await attestPublishedJourneys(trustId, publishedReviewItems.map(({ id, contentFingerprint }) => ({ journeyId: id, contentFingerprint })));
      const next = saveTrustConfig(trustId, response.configuration);
      setTrustConfig(next);
      setJourneyDraft(buildDraft(selectedTemplate, next));
      setBulkReviewConfirmed(false);
      setNotice(`${publishedReviewItems.length} current published Journeys now have your dated clinical-review record. Standard and Large Text PDF export should be available for these source versions.`);
    } catch (error) {
      setNotice(error.message || 'The attestation could not be saved. No clinical reviews were recorded.');
    } finally {
      setBulkReviewBusy(false);
    }
  };

  const applyReviewConfiguration = (configuration) => {
    if (!configuration) return;
    const saved = saveTrustConfig(trustId, configuration);
    setTrustConfig(saved);
    setJourneyDraft(buildDraft(selectedTemplate, saved));
  };

  const changeCount = configured && journeyDraft ? countJourneyOverlayChanges(genericJourney, journeyDraft) : 0;
  const previewPayload = configured && journeyDraft ? compactJourneyOverlay(genericJourney, journeyDraft) : null;
  // Unlisted safety-related journeys do not accept publicly shareable, unsigned
  // ?preview= overlays; local links display only after approved publication.
  const configuredPreviewUrl = !selectedTemplate?.unlisted && previewPayload && !selectedTemplate?.clinicalReleaseHold
    ? `${patientAppBaseUrl}${configurableJourneyRoute(selectedTemplate.id)}?preview=${encodeURIComponent(encodeJourneyPreviewPayload(previewPayload))}`
    : null;

  return <div className="journey-studio-shell">
    <header className="journey-studio-header">
      <div>
        <p className="eyebrow">{reviewOnly ? 'Clinical assurance' : 'Trust configuration'}</p>
        <h1>{reviewOnly ? 'Journey content review' : 'Configure Journeys'}</h1>
        <p>{reviewOnly ? 'Choose one configured Journey at a time, review all patient-facing clinical wording, then approve it or request changes.' : 'Choose one Journey, review the content, confirm the people shown, add Trust information links, then save and move to the next Journey.'}</p>
      </div>
      <label className="context-select"><select value={trustId} onChange={(event) => { if (!dirty || window.confirm('Discard unsaved changes and switch Trust?')) loadTrust(event.target.value); }}>{scopedTrusts.map((trust) => <option key={trust.id} value={trust.id}>{trust.shortName}</option>)}</select></label>
    </header>

    <div className="journey-studio-layout">
      <aside className="journey-studio-sidebar">
        {workspaceSwitcher}
        <div className="journey-studio-stat"><LibraryBig size={20}/><div><strong>{configuredCount} configured</strong><span>{journeyLimit ? `${journeyLimit} Journey licence` : 'No Journey limit set'}</span></div></div>
        <div className="journey-licence-note"><strong>Journey access</strong><p>{journeyLimit ? `This Trust can configure up to ${journeyLimit} Journey${journeyLimit === 1 ? '' : 's'}. ${Math.max(0, journeyLimit - configuredCount)} remaining.` : 'Journey availability is governed by the Trust’s Know Your Care licence. No limit is currently set for this Trust.'}</p></div>
      </aside>

      <main className="journey-library-main">
        {notice && <div className="studio-notice" role="status"><span>{notice}</span><button onClick={() => setNotice('')} aria-label="Dismiss notification">×</button></div>}
        {locked && <div className="workflow-lock-banner"><LibraryBig/><div><strong>Trust profile locked</strong><span>Journey configuration is locked. {trustConfig.status === 'published' ? 'Clinical reviewers can still record review decisions on the current wording without changing the live release.' : 'New review decisions require the profile to be returned to draft.'}</span></div></div>}

        {canAttestPublished ? <section className="trust-config-panel" aria-label="Published Trust Journey clinical sign-off">
          <h2>Clinically review all published Journeys</h2>
          <p>The following <strong>{publishedReviewItems.length}</strong> Journeys are in the current published Trust configuration. This action records your individual, authenticated sign-off for their existing wording and local Trust links. It does not change the published patient content or approve national Neonatal drafts.</p>
          <ul>{publishedReviewItems.map(item => <li key={item.id}>{item.title} — {trustConfig.journeys?.[item.id]?.clinicalReview?.status === 'reviewed_accurate' && trustConfig.journeys?.[item.id]?.clinicalReview?.contentFingerprint === item.contentFingerprint ? 'Already reviewed' : 'Review needed'}</li>)}</ul>
          <label className="journey-bulk-attestation"><input type="checkbox" checked={bulkReviewConfirmed} onChange={event => setBulkReviewConfirmed(event.target.checked)} />
            <span>I confirm that I have clinically reviewed the current patient-facing content, care stages and local links for every Journey listed above and consider them accurate for this Trust.</span>
          </label>
          <div className="trust-actions"><button type="button" className="button primary" disabled={!bulkReviewConfirmed || bulkReviewBusy || dirty} onClick={confirmPublishedReviews}>
            {bulkReviewBusy ? 'Recording review…' : `Record clinical review for all ${publishedReviewItems.length} Journeys`}
          </button></div>
        </section> : null}

        <section className="journey-selector-panel">
          <label>Journey to {reviewOnly ? 'review' : 'configure'}
            <select value={selectedTemplate?.id || ''} onChange={(event) => selectJourney(event.target.value)}>
              {Object.entries(JOURNEY_CATEGORIES).map(([key, label]) => {
                const group = availableTemplates.filter((journey) => journey.category === key);
                if (!group.length) return null;
                return <optgroup key={key} label={label}>{group.map((journey) => <option key={journey.id} value={journey.id}>{journey.title}{trustConfig.journeys?.[journey.id] ? ' — configured' : ''}</option>)}</optgroup>;
              })}
            </select>
          </label>
          {selectedTemplate && <div className="journey-selector-meta"><strong>{JOURNEY_CATEGORIES[selectedTemplate.category]}</strong><span>{configured ? `Configured · ${changeCount} local change${changeCount === 1 ? '' : 's'}` : 'Not yet configured for this Trust'}</span></div>}
        </section>

        {!selectedTemplate || !genericJourney || !journeyDraft ? <section className="trust-config-panel"><p>{reviewOnly ? 'No configured Journeys are assigned to your clinical-review scope. Ask a Platform Admin to assign the appropriate Journeys.' : 'No Journey is available in this workspace.'}</p></section> : <article className={`journey-library-card journey-single-card ${configured ? 'configured' : ''}`}>
          <div className="journey-card-topline"><span>{JOURNEY_CATEGORIES[selectedTemplate.category]}</span>{configured && <strong>Added to Trust</strong>}</div>
          <h2>{journeyDraft.title || genericJourney.title}</h2>
          <p>{configured || reviewOnly ? genericJourney.introduction : (selectedTemplate.summary || genericJourney.introduction)}</p>
          {selectedTemplate.clinicalReleaseHold ? <div className="workflow-lock-banner" role="note"><LibraryBig/><div><strong>Awaiting national neonatal clinical review</strong><span>This generic seven-section Journey is available for draft Trust configuration, local contacts and clinical review. National clinical text and machine translations are not approved. Trust publication is blocked until the national review is completed.</span></div></div> : null}
          {selectedTemplate.category === 'nhnn_pilot' ? <div className="workflow-lock-banner" role="note"><LibraryBig/><div><strong>Existing NHNN demonstration imported</strong><span>This starting point retains the original stages, patient questions, clinical explanations, people and UCLH links. Studio currently supports edits to titles, stage summaries, role inclusion and links. Detailed explanation and question editing requires a further editor enhancement. It remains a private, unapproved draft until clinical review and explicit publication.</span></div></div> : null}
          {selectedTemplate.unlisted ? <div className="workflow-lock-banner"><LibraryBig/><div><strong>Unlisted clinical-review journey</strong><span>The patient route is not shown on JourneyInfo. Wording and safety guidance are fixed. Approved local contact and resource links will appear only after clinical review and published Trust release. The link is not a security barrier.</span></div></div> : null}

          {!configured && !reviewOnly ? <div className="journey-add-panel">
            <div className="journey-selection-summary" aria-label="Journey overview">
              <span><strong>{genericJourney.stages?.length || 0}</strong> stages</span>
              <span><strong>{genericJourney.roles?.length || 0}</strong> core roles</span>
              <span><strong>{genericJourney.usefulLinks?.length || 0}</strong> Trust links</span>
            </div>
            {limitReached ? <div className="workflow-lock-banner"><LibraryBig/><div><strong>Journey licence limit reached</strong><span>This Trust is licensed for {journeyLimit} Journey{journeyLimit === 1 ? '' : 's'}. A Platform Admin must increase the Journey limit before another can be added.</span></div></div> : null}
            <div className="journey-card-actions">
              <button className="button primary" type="button" disabled={locked || saving || limitReached} onClick={addJourney}><Plus size={16}/>Add this Journey</button>
              {selectedTemplate.clinicalReleaseHold ? <span className="journey-status" title="Open the isolated neonatal editorial preview for review only">Editorial preview only — not published</span> : <a className="button secondary" href={`${patientAppBaseUrl}${configurableJourneyRoute(selectedTemplate.id)}`} target="_blank" rel="noreferrer">Preview generic <ExternalLink size={16}/></a>}
              <button className="button secondary" type="button" onClick={nextJourney}>Next Journey <ArrowRight size={16}/></button>
            </div>
          </div> : <>

            <JourneyClinicalSummaryReview
              trustId={trustId}
              journeyId={selectedTemplate.id}
              journey={applyJourneyOverlay(genericJourney, journeyDraft)}
               reviewSource={savedOverlay}
              reviewRecord={savedOverlay?.clinicalReview}
              studioAccess={studioAccess}
              locked={reviewLocked}
              onSaved={applyReviewConfiguration}
            />

            {!reviewOnly && <details className="journey-workflow-section">
              <summary><span><strong>2. Confirm roles included</strong><small>Choose which members of the care team should appear in this Journey.</small></span><span className="journey-status">{(genericJourney.roles || []).filter((role) => journeyDraft.roleOverrides?.[getRoleId(role)] !== false).length} shown</span></summary>
              <div className="journey-workflow-body">
                <div className="journey-role-checks">{(genericJourney.roles || []).map((role) => {
                  const id = getRoleId(role);
                  if (!id) return null;
                  return <label key={id}><input type="checkbox" checked={journeyDraft.roleOverrides?.[id] !== false} onChange={(event) => updateDraft({ roleOverrides: { [id]: event.target.checked } })}/><span>{getRoleTitle(role)}</span></label>;
                })}</div>
              </div>
            </details>}

            {!reviewOnly && <details className="journey-workflow-section">
              <summary><span><strong>3. Add Trust information and links</strong><small>Link patients to approved local information relevant to this Journey.</small></span><span className="journey-status">{(() => { const links = genericJourney.usefulLinks || []; const active = links.filter((link) => journeyDraft.linkOverrides?.[link.id]?.visible !== false); const done = active.filter((link) => Boolean(journeyDraft.linkOverrides?.[link.id]?.url)); return `${done.length}/${active.length} links`; })()}</span></summary>
              <div className="journey-workflow-body">
                <div className="journey-config-fields">
                  <label>Hospital or service<input value={journeyDraft.serviceName || ''} onChange={(event) => updateDraft({ serviceName: event.target.value })}/></label>
                  <label>Trust display name<input value={journeyDraft.branding?.trustName || ''} onChange={(event) => updateDraft({ branding: { trustName: event.target.value } })}/></label>
                  <label>Logo URL<input value={journeyDraft.branding?.logoUrl || ''} placeholder="Optional https://…" onChange={(event) => updateDraft({ branding: { logoUrl: event.target.value } })}/></label>
                  <label>Logo alt text<input value={journeyDraft.branding?.logoAlt || ''} onChange={(event) => updateDraft({ branding: { logoAlt: event.target.value } })}/></label>
                </div>
                <div className="journey-link-overrides">{(genericJourney.usefulLinks || []).map((link) => {
                  const local = journeyDraft.linkOverrides?.[link.id] || { title: link.title, description: link.description || '', url: '', visible: true };
                  const visible = local.visible !== false;
                  return <div key={link.id}>
                    <div className="journey-card-topline"><strong>{link.title}</strong><label><input type="checkbox" disabled={selectedTemplate.unlisted && link.id === 'hospital-team'} checked={selectedTemplate.unlisted && link.id === 'hospital-team' ? true : visible} onChange={(event) => updateDraft({ linkOverrides: { [link.id]: { ...local, visible: event.target.checked } } })}/><span>Show to patients</span></label></div>
                    <label>Display title<input value={local.title || ''} disabled={!visible || selectedTemplate.unlisted} onChange={(event) => updateDraft({ linkOverrides: { [link.id]: { ...local, title: event.target.value } } })}/></label>
                    <label>Short description<textarea rows="2" value={local.description || ''} disabled={!visible || selectedTemplate.unlisted} onChange={(event) => updateDraft({ linkOverrides: { [link.id]: { ...local, description: event.target.value } } })}/></label>
                    <label>Trust URL<input value={local.url || ''} disabled={!visible} placeholder="https://…" onChange={(event) => updateDraft({ linkOverrides: { [link.id]: { ...local, url: event.target.value } } })}/></label>
                  </div>;
                })}</div>
              </div>
            </details>}

            <div className="journey-save-footer">
              <div><strong>{dirty ? 'Unsaved changes' : 'All changes saved'}</strong><small>{reviewOnly ? 'Clinical review decisions are stored with the Trust configuration.' : 'Save this Journey before moving on.'}</small></div>
              <div className="trust-actions">
                {!reviewOnly && <button className="button primary" type="button" disabled={locked || saving || !dirty} onClick={() => persistJourney()}><Save size={16}/>{saving ? 'Saving…' : 'Save changes'}</button>}
                {configuredPreviewUrl && <a className="button secondary" href={configuredPreviewUrl} target="_blank" rel="noreferrer">Preview configured <ExternalLink size={16}/></a>}
                <button className="button secondary" type="button" disabled={saving} onClick={nextJourney}>{dirty && !reviewOnly ? 'Save & next Journey' : 'Next Journey'} <ArrowRight size={16}/></button>
                {!reviewOnly && configured && <button className="button danger-quiet" type="button" disabled={locked || saving} onClick={removeJourney}><Trash2 size={16}/>Remove Journey</button>}
              </div>
            </div>
          </>}
        </article>}
      </main>
    </div>
  </div>;
}
